import React, { useContext } from 'react';
import Styled from 'styled-components';
import Link from 'next/link';
import { Trans } from '@lingui/react';
import { format } from 'date-fns';
import { sv, nb, fi, enGB } from 'date-fns/locale';
import { Container, Flex } from '@components/layout/Grid';
import ShareModal from '@organisms/modals/ShareModal';
import { LiveEvent, LiveEventPaymentKind } from '@gql/generated';
import LanguageContext from '@lib/contexts/languageContext';
import { getFormattedPrice } from '@ecosystems/events/EventListingCard';
import { Button } from '@components/ButtonV2';

const ReserveSection = Styled(Container)`
  padding: 20px;
  margin-bottom: 40px;
  background-color: #F0F5FB;

  @media(min-width: ${({ theme }) => theme.viewports.tablet}px){
    padding-left: 50px;
    padding-right: 50px;
  }
`;

const Title = Styled.h2`
  font-size: 24px;
  font-weight: 500;
  margin: 0;
`;

const Description = Styled.p`
  font-size: 16px;
  margin: 24px 0 30px;
`;

const BannerImage = Styled.img`
  display: none;
  max-width: 50%;
  max-height: 350px;
  object-fit: contain;
  margin-left: 50px;
  transform: translateY(40px);


  @media(min-width: ${({ theme }) => theme.viewports.laptop}px){
    display: block;
  }
`;

const ShareEventButton = Styled.button`
  font-size: 13px;
  color: rgb(36,50,64);
  font-weight: 600;
  border: 1px solid rgb(36,50,64);
  border-radius: 5px;
  padding: 10px 24px;
  margin-top: 10px;
  background-color: transparent;
  cursor: pointer;

  @media(min-width: ${({ theme }) => theme.viewports.mobile}px){
    margin-top: 0;
    margin-left: 10px;
  }
`;

const localeMap = {
  en: enGB,
  se: sv,
  no: nb,
  fi,
};

type Props = {
  event: LiveEvent;
  userHasAccess: boolean;
};

const ReserveBanner = ({ event, userHasAccess }: Props) => {
  const lang = useContext(LanguageContext);
  const registrationEnd = new Date(event.registrationEndDate);

  const formatedRegistrationEnd = format(
    registrationEnd,
    "dd MMMM yyyy 'at' HH:mm OOOO",
    { locale: localeMap[lang] }
  );

  const firstEventDate = format(
    new Date(event.eventDates[0]),
    "EEEE dd MMMM yyyy 'at' HH:mm OOOO",
    { locale: localeMap[lang] }
  );

  let price = null;
  if (event.paymentKind === LiveEventPaymentKind['Flexible']) {
    price = getFormattedPrice(event);
  } else if (event.price?.originalPrice === 0) {
    price = <Trans id="live.event_page.event_free" />;
  } else {
    price = getFormattedPrice(event);
  }

  const isExpired = event.isExpired;
  const isComingSoon = event.isComingSoon;

  let description = null,
    action_button = (
      <Button href={`/${lang}/live`} appearance="dark-blue">
        <Trans id="live.event_page.reserve_spot_banner.browse_events" />
      </Button>
    );
  if (userHasAccess) {
    description = (
      <>
        <Trans
          id="live.event_page.reserve_spot_banner.reserved_desc"
          values={{ event_name: event.title }}
          components={[<b key="0"></b>]}
        />
        <br />
        <Trans
          id="live.event_page.reserve_spot_banner.event_date"
          values={{ date: firstEventDate }}
        />
      </>
    );
  } else if (isExpired) {
    description = (
      <Trans
        id="live.event_page.reserve_spot_banner.expired_desc"
        values={{ event_name: event.title }}
        components={[<b key="0"></b>]}
      />
    );
  } else {
    description = (
      <>
        <Trans
          id="live.event_page.reserve_spot_banner.desc"
          values={{ event_name: event.title }}
          components={[<b key="0"></b>]}
        />
        <br />
        <Trans
          id="live.event_page.reserve_spot_banner.last_registration_date"
          values={{ endDate: formatedRegistrationEnd }}
        />
      </>
    );

    action_button = (
      <Button
        href={`/${lang}/live/${event.slug}/reserve`}
        disabled={isExpired || isComingSoon}
        appearance="dark-blue"
      >
        <Trans id="live.event_page.reserve_spot_banner.book_btn" />: {price}
      </Button>
    );
  }

  return (
    <ReserveSection data-testid="LiveEvent--ReserveBanner">
      <Flex
        flex={1}
        flexDirection="column"
        justifyContent="center"
        py={[20, 20, 0]}
      >
        <Title>
          {userHasAccess ? (
            <Trans id="live.event_page.reserve_spot_banner.reserved_title" />
          ) : (
            <Trans id="live.event_page.reserve_spot_banner.title" />
          )}
        </Title>
        <Description>{description}</Description>
        <Flex flexDirection={['column', 'row', 'row']}>
          {action_button}
          {!(isExpired || isComingSoon) ? (
            <ShareModal
              subject={event.title}
              resource="live"
              resourceIdentifier={event.slug}
            >
              {openModal => (
                <ShareEventButton onClick={openModal}>
                  <Trans id="live.event_page.reserve_spot_banner.share_btn" />
                </ShareEventButton>
              )}
            </ShareModal>
          ) : null}
        </Flex>
      </Flex>
      <BannerImage src={event.thumbnail} />
    </ReserveSection>
  );
};

export default ReserveBanner;
